<template>
  <Card class="mb-0 gap-2 py-3" data-testid="dashboard-pipeline-reviews-card">
    <div class="border-b border-gray-200 px-4 pb-2">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <CardTitle>Pipeline reviews</CardTitle>
            <Badge v-if="!emptyState" variant="gray">{{ pipelineReviews.completed }}</Badge>
          </div>
        </div>

        <div v-if="!emptyState" class="flex flex-wrap items-center gap-3">
          <Popover v-model:open="timeframePopoverOpen">
            <PopoverTrigger as-child>
              <button class="dashboard-filter-trigger" data-testid="dashboard-pipeline-timeframe-trigger">
                <CalendarRange class="h-3.5 w-3.5" />
                <span>{{ timeframeLabel }}</span>
                <ChevronDown class="h-3.5 w-3.5 text-gray-400" />
              </button>
            </PopoverTrigger>

            <PopoverContent align="end" class="w-72 p-1.5" :side-offset="8">
              <div class="space-y-1">
                <button
                  v-for="option in timelineOptions"
                  :key="option"
                  class="dashboard-filter-row"
                  :class="{ 'dashboard-filter-row-active': metricsTimeline === option }"
                  @click="selectTimeline(option)"
                >
                  <span>{{ option }}</span>
                  <CheckIcon v-if="metricsTimeline === option" class="h-3.5 w-3.5" />
                </button>
              </div>

              <div class="mt-1 border-t border-gray-100 px-2 pt-2.5">
                <p class="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">Custom range</p>

                <div class="space-y-2">
                  <DatePicker
                    :model-value="customStart || undefined"
                    @update:model-value="emit('update:customStart', $event ?? null)"
                  />
                  <DatePicker
                    :model-value="customEnd || undefined"
                    @update:model-value="emit('update:customEnd', $event ?? null)"
                  />
                </div>

                <Button
                  class="mt-2 w-full"
                  variant="dark"
                  :disabled="!customStart || !customEnd"
                  @click="applyCustomTimeframe"
                >
                  Apply custom range
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Popover v-model:open="reviewerPopoverOpen">
            <PopoverTrigger as-child>
              <button class="dashboard-filter-trigger" data-testid="dashboard-pipeline-reviewer-trigger">
                <ListFilterIcon class="h-3.5 w-3.5" />
                <span>{{ reviewerLabel }}</span>
                <ChevronDown class="h-3.5 w-3.5 text-gray-400" />
              </button>
            </PopoverTrigger>

            <PopoverContent align="end" class="w-64 p-1.5" :side-offset="8">
              <button
                class="dashboard-filter-row"
                :class="{ 'dashboard-filter-row-active': pipelineReviewerFilter === 'All' }"
                @click="selectReviewer('All')"
              >
                <span>All reviewers</span>
                <CheckIcon v-if="pipelineReviewerFilter === 'All'" class="h-3.5 w-3.5" />
              </button>
              <button
                v-for="reviewer in eligiblePipelineReviewers"
                :key="reviewer"
                class="dashboard-filter-row"
                :class="{ 'dashboard-filter-row-active': pipelineReviewerFilter === reviewer }"
                @click="selectReviewer(reviewer)"
              >
                <span>{{ reviewer }}</span>
                <CheckIcon v-if="pipelineReviewerFilter === reviewer" class="h-3.5 w-3.5" />
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>

    <CardContent class="px-0">
      <div v-if="emptyState" class="flex flex-1 items-center justify-center px-6 py-10">
        <div class="flex max-w-sm flex-col items-center text-center">
          <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <ClipboardList class="h-5 w-5" />
          </div>
          <p class="mt-4 text-sm font-medium text-slate-900">{{ emptyState.title }}</p>
          <p class="mt-1 text-sm text-slate-500">{{ emptyState.description }}</p>
          <Button as-child size="sm" variant="outline" class="mt-4">
            <router-link to="/settings/workflows/pipeline-reviews">{{ emptyState.ctaLabel }}</router-link>
          </Button>
        </div>
      </div>

      <div v-else class="grid gap-4 border-b border-gray-200 px-4 py-2.5 sm:grid-cols-2">
        <div>
          <p class="text-xs text-slate-600">Completed</p>
          <p class="mt-1 text-2xl font-semibold text-slate-900">{{ pipelineReviews.completed }}</p>
        </div>

        <div>
          <p class="text-xs text-slate-600">Completion rate</p>
          <p class="mt-1 text-2xl font-semibold text-slate-900">
            {{ (pipelineReviews.completion_rate * 100).toFixed(0) }}%
          </p>
        </div>
      </div>

      <div v-if="!emptyState">
        <div v-for="row in scoreRows" :key="row.score" class="border-b border-gray-200 px-4 py-2.5 last:border-b-0">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <PipelinePerformanceSmile :score="row.score" />
              <p class="text-sm font-medium text-slate-900">Score {{ row.score }}</p>
            </div>
            <div class="text-right text-sm text-slate-600">
              <span class="font-semibold text-slate-900">{{ row.count }}</span>
              <span class="ml-1">({{ row.percentage.toFixed(0) }}%)</span>
            </div>
          </div>

          <div class="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div class="h-full rounded-full bg-slate-700" :style="{ width: `${row.width}%` }"></div>
          </div>
        </div>

        <div v-if="unscoredRow" class="border-b border-gray-200 px-4 py-2.5 last:border-b-0">
          <div class="flex items-center justify-between gap-3">
            <p class="text-sm font-medium text-slate-900">Unscored</p>
            <div class="text-right text-sm text-slate-600">
              <span class="font-semibold text-slate-900">{{ unscoredRow.count }}</span>
              <span class="ml-1">({{ unscoredRow.percentage.toFixed(0) }}%)</span>
            </div>
          </div>

          <div class="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div class="h-full rounded-full bg-slate-300" :style="{ width: `${unscoredRow.width}%` }"></div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { DashboardMetricsTimeline, DashboardPipelineReviews } from '@/api/dashboard';
import PipelinePerformanceSmile from '@/components/shared/pipeline_reviews/ScoreIcon.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date_picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTeamStore } from '@/stores/team';
import { CalendarRange, CheckIcon, ChevronDown, ClipboardList, ListFilterIcon } from 'lucide-vue-next';

const props = defineProps<{
  pipelineReviews: DashboardPipelineReviews;
  metricsTimeline: DashboardMetricsTimeline;
  customTimeframeLabel: string;
  customStart: string | null;
  customEnd: string | null;
  pipelineReviewerFilter: string;
  eligiblePipelineReviewers: string[];
}>();

const emit = defineEmits<{
  setTimeline: [value: DashboardMetricsTimeline];
  'update:customStart': [value: string | null];
  'update:customEnd': [value: string | null];
  applyCustomTimeframe: [];
  'update:pipelineReviewerFilter': [value: string];
}>();

const teamStore = useTeamStore();

const emptyState = computed(() => {
  if (teamStore.team.pipeline_review_status === 'not_configured') {
    return {
      title: 'Pipeline reviews are off',
      description: 'See how your team is really doing. Get insights live or whenever your agents have time.',
      ctaLabel: 'Turn on pipeline reviews',
    };
  }

  if (teamStore.team.pipeline_review_status === 'paused') {
    return {
      title: 'Pipeline reviews are paused',
      description: "...and so is your view into how your team's really doing. Start hearing from them again.",
      ctaLabel: 'Resume pipeline reviews',
    };
  }

  return null;
});

const timeframePopoverOpen = ref(false);
const reviewerPopoverOpen = ref(false);
const timelineOptions: DashboardMetricsTimeline[] = ['This Month', 'Last Month', 'This Year', 'All Time'];

const maxRowValue = computed(() =>
  Math.max(...Object.values(props.pipelineReviews.score_distribution), props.pipelineReviews.unscored, 1)
);

const scoreRows = computed(() => {
  const values = props.pipelineReviews.score_distribution;
  const max = maxRowValue.value;

  return (['5', '4', '3', '2', '1'] as const).map(score => {
    const count = values[score];
    return {
      score: Number(score),
      count,
      width: count > 0 ? Math.max((count / max) * 100, 12) : 0,
      percentage: props.pipelineReviews.completed > 0 ? (count / props.pipelineReviews.completed) * 100 : 0,
    };
  });
});

const unscoredRow = computed(() => {
  const count = props.pipelineReviews.unscored;
  if (count <= 0) return null;
  return {
    count,
    width: Math.max((count / maxRowValue.value) * 100, 12),
    percentage: props.pipelineReviews.completed > 0 ? (count / props.pipelineReviews.completed) * 100 : 0,
  };
});

const handlePipelineReviewerChange = (value: string | number | null | undefined) => {
  if (value == null) return;

  emit('update:pipelineReviewerFilter', String(value));
};

const timeframeLabel = computed(() =>
  props.metricsTimeline === 'Custom' ? props.customTimeframeLabel : props.metricsTimeline
);

const reviewerLabel = computed(() =>
  props.pipelineReviewerFilter === 'All' ? 'All reviewers' : props.pipelineReviewerFilter
);

const selectTimeline = (timeline: DashboardMetricsTimeline) => {
  if (timeline === 'Custom') return;

  timeframePopoverOpen.value = false;
  emit('setTimeline', timeline);
};

const applyCustomTimeframe = () => {
  timeframePopoverOpen.value = false;
  emit('applyCustomTimeframe');
};

const selectReviewer = (reviewer: string) => {
  reviewerPopoverOpen.value = false;
  handlePipelineReviewerChange(reviewer);
};
</script>

<style scoped>
@reference "tailwindcss";

.dashboard-filter-trigger {
  @apply inline-flex items-center gap-1.5 rounded-md px-0 py-0 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900;
}

.dashboard-filter-row {
  @apply flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900;
}

.dashboard-filter-row-active {
  @apply bg-gray-100 font-medium text-gray-900;
}
</style>
